import qs from "qs";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";

import { LocalStorageKeys, TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useContentItems } from "@is/hooks";
import { BasketItemRequest, useManageBasket } from "@is/hooks/basket";
import { LocalStorageService } from "@is/services";
import { BasketItem, CheckoutConfiguration } from "@is/types";
import { convertBasketItemToProductItem } from "@is/utils";
import { rememberQRCheckoutSession } from "@is/utils/session";

const useManageBasketItems = (
  setItemPending,
  enabledQRCodeCheckoutConfiguration?: CheckoutConfiguration
) => {
  const [basketItems, setBasketItems] = useState<BasketItem[] | null>([]);
  const {
    handleUpdateBasket,
    basketItems: items,
    error,
    totalItems,
    totalPrice,
  } = useManageBasket();
  const { t } = useTranslation(TranslationContext.Basket);

  const { qrCodeCheckout, updateViewState } =
    useContext(ViewStateContext).content;

  const { handleOpenProductDetails } = useContentItems();

  useEffect(() => {
    if (items) {
      setBasketItems(items);
    }
  }, [items]);

  useEffect(() => {
    if (error) {
      setBasketItems(items);
    }
  }, [error, items]);

  const currencyCode = useMemo(
    () => (!totalItems ? "" : basketItems[0]?.price?.currencyCode),
    [basketItems, totalItems]
  );

  const sessionId = LocalStorageService.getItem(LocalStorageKeys.SessionId);

  // The QR the shopper carries away is minted for THIS session; the websocket
  // that reports their payment is device-scoped, so the correlation has to be
  // kept here (ticket aecd58ce). Gated on the QR actually being offered — the
  // same condition `BasketItems` renders it under — because this hook also
  // backs the checkout and summary lists, which show no QR: writing there
  // would overwrite an outstanding QR's session with a later one.
  useEffect(() => {
    if (!enabledQRCodeCheckoutConfiguration) {
      return;
    }

    rememberQRCheckoutSession(sessionId);
  }, [enabledQRCodeCheckoutConfiguration, sessionId]);

  const qrCodeUrl = useMemo(() => {
    const languageCode = LocalStorageService.getItem(
      LocalStorageKeys.LanguageCode
    );
    const tenantId = LocalStorageService.getItem(LocalStorageKeys.TenantId);

    const queryString = qs.stringify({
      tenantId,
      languageCode,
      sessionId,
      checkoutConfigurationId: enabledQRCodeCheckoutConfiguration?.id,
    });

    return `${window.location.origin}/instore/forward-basket?${queryString}`;
  }, [enabledQRCodeCheckoutConfiguration, sessionId]);

  const debouncedUpdateBasket = useDebouncedCallback(
    (items: BasketItemRequest[]) => {
      handleUpdateBasket({ items });

      const quantity = items.reduce(
        (previous, current) => previous + current.quantity,
        0
      );

      if (quantity > 0) setItemPending(false);
    },
    500
  );

  const handleChangeItemQuantity = useCallback(
    (ean: string, newQuantity: number) => {
      setItemPending(true);

      const newItems = basketItems?.map((item) => {
        if (item.ean === ean) {
          return {
            ...item,
            quantity: newQuantity,
          };
        }

        return item;
      });

      const newItemsWithQuantity = newItems?.filter(
        (item) => item.quantity > 0
      );

      setBasketItems(newItemsWithQuantity);

      const newBasketItems = newItems?.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        ean: item.ean,
        price: item.price,
        variantId: item.variantId,
      }));

      debouncedUpdateBasket(newBasketItems);
    },
    [basketItems, debouncedUpdateBasket, setItemPending]
  );

  const handleRemoveItem = useCallback(
    (ean: string) => {
      const newItems = basketItems?.map((item) => ({
        ...item,
        quantity: item.ean === ean ? 0 : item.quantity,
      }));

      const filteredBasketItems = newItems?.filter((item) => item.ean !== ean);

      setBasketItems(filteredBasketItems);

      const newBasketItems = newItems?.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        ean: item.ean,
        price: item.price,
        variantId: item.variantId,
      }));

      handleUpdateBasket({ items: newBasketItems });
    },
    [basketItems, handleUpdateBasket]
  );

  const handleClickItem = useCallback(
    (item: BasketItem) => {
      handleOpenProductDetails({
        productItem: convertBasketItemToProductItem(item),
      });
    },
    [handleOpenProductDetails]
  );

  const handleQRCodeModalOpen = useCallback(() => {
    updateViewState({ showQRCodeModal: true });
  }, [updateViewState]);

  return {
    totalPrice,
    totalItems,
    currencyCode,
    basketItems,
    handleChangeItemQuantity,
    handleRemoveItem,
    handleClickItem,
    handleOpenQRCodeModal: handleQRCodeModalOpen,
    qrCodeUrl,
    qrCheckoutInProgress: qrCodeCheckout.inProgress,
    t,
  };
};

export default useManageBasketItems;
